import { getDataAPI } from './fetchData';
import { remindersApi } from './remindersApi';

/**
 * Timeline API Service
 * Aggregates upcoming events from different apps to create a forward-looking timeline
 */

const DEFAULT_SETTINGS = {
  upcomingShows: true,
  upcomingReleases: true,
  tasksDeadlines: true,
  flights: true,
  hotels: true,
  newShowOffers: true,
  ticketCountUpdates: true,
  releaseWorkflowTasks: true
};

/**
 * Get timeline settings from localStorage
 */
const getTimelineSettings = () => {
  try {
    const saved = localStorage.getItem('timelineSettings');
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error loading timeline settings:', error);
    return DEFAULT_SETTINGS;
  }
};

/**
 * Categorize dates into groups: "Today", "Tomorrow", "This Week", "Next Week", or specific date
 */
const getDateGroup = (date) => {
  if (!date) return 'Unknown';
  
  const now = new Date();
  const eventDate = new Date(date);
  
  // Reset time to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  
  const diffDays = Math.floor((eventDay - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return 'Past';
  } else if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays <= 7) {
    return 'This Week';
  } else if (diffDays <= 14) {
    return 'Next Week';
  } else {
    // Return formatted date for dates further out
    return eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: eventDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  }
};

/**
 * Generate intelligent time-based messaging
 */
const getTimeContext = (date, type = 'event') => {
  if (!date) return '';
  
  const now = new Date();
  const eventDate = new Date(date);
  const diffMs = eventDate - now;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 0) {
    return 'Past';
  } else if (diffMinutes < 60) {
    return diffMinutes <= 1 ? 'Now' : `in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    if (diffHours === 1) {
      return 'in 1 hour';
    } else if (eventDate.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString()) {
      return 'Tomorrow';
    } else {
      return `in ${diffHours} hours`;
    }
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays <= 7) {
    return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffDays <= 14) {
    return 'Next Week';
  } else {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? 'Next Week' : `in ${weeks} weeks`;
  }
};

/**
 * Process bookings data for timeline
 */
const processBookingsForTimeline = (bookingsData, settings) => {
  if (!settings.upcomingShows && !settings.newShowOffers && !settings.ticketCountUpdates) {
    return [];
  }
  
  const bookings = Array.isArray(bookingsData) ? bookingsData : (bookingsData?.data || []);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const timelineItems = [];
  
  bookings.forEach((booking) => {
    if (!booking.perf_date) return;
    
    const perfDate = new Date(booking.perf_date);
    perfDate.setHours(0, 0, 0, 0);
    
    // Only include future or today's shows
    if (perfDate < today) return;
    
    const status = booking.status?.toLowerCase() || 'pending';
    const isConfirmed = status === 'confirmed';
    const isPending = status === 'pending' || !isConfirmed;
    
    // Upcoming Shows (Confirmed)
    if (settings.upcomingShows && isConfirmed) {
      const timeContext = getTimeContext(booking.perf_date);
      const dateGroup = getDateGroup(booking.perf_date);
      
      timelineItems.push({
        id: `show-${booking._id}`,
        type: 'show',
        subType: 'confirmed',
        title: booking.event_title || booking.venue || 'Show',
        description: `${booking.venue || 'Venue TBD'} - ${booking.city_state || booking.location || 'Location TBD'}`,
        date: booking.perf_date,
        dateGroup,
        timeContext,
        status: 'confirmed',
        color: '#FFFF00',
        actionUrl: '/bookings',
        actionLabel: 'Bookings',
        metadata: {
          venue: booking.venue,
          artist: booking.artist,
          location: booking.city_state || booking.location,
          ticketsSold: booking.tix_sold || booking.current_ticket_count || 0,
          capacity: booking.sellable_cap,
          promoter: booking.promoter,
          status: booking.status
        }
      });
    }
    
    // New Show Offers (Pending)
    if (settings.newShowOffers && isPending) {
      const timeContext = getTimeContext(booking.perf_date);
      const dateGroup = getDateGroup(booking.perf_date);
      
      timelineItems.push({
        id: `offer-${booking._id}`,
        type: 'show',
        subType: 'offer',
        title: 'New Show Offer',
        description: `${booking.venue || 'Venue TBD'} - ${booking.city_state || booking.location || 'Location TBD'}`,
        date: booking.perf_date,
        dateGroup,
        timeContext,
        status: 'pending',
        color: '#FFFF00',
        actionUrl: '/bookings',
        actionLabel: 'Bookings',
        metadata: {
          venue: booking.venue,
          artist: booking.artist,
          location: booking.city_state || booking.location,
          status: booking.status
        }
      });
    }
    
    // Ticket Count Updates (for confirmed shows)
    if (settings.ticketCountUpdates && isConfirmed && booking.tix_sold !== undefined) {
      const timeContext = getTimeContext(booking.perf_date);
      const dateGroup = getDateGroup(booking.perf_date);
      
      timelineItems.push({
        id: `tickets-${booking._id}`,
        type: 'ticket-update',
        title: 'Ticket Count Update',
        description: `${booking.venue || 'Venue TBD'}: ${booking.tix_sold || 0} sold${booking.sellable_cap ? ` of ${booking.sellable_cap}` : ''}`,
        date: booking.perf_date,
        dateGroup,
        timeContext,
        status: 'update',
        color: '#FFFF00',
        actionUrl: '/bookings',
        actionLabel: 'Bookings',
        metadata: {
          venue: booking.venue,
          ticketsSold: booking.tix_sold || 0,
          capacity: booking.sellable_cap
        }
      });
    }
  });
  
  return timelineItems;
};

/**
 * Process catalog data for timeline (upcoming releases)
 */
const processCatalogForTimeline = (catalogData, settings) => {
  if (!settings.upcomingReleases && !settings.releaseWorkflowTasks) {
    return [];
  }
  
  const catalog = Array.isArray(catalogData) ? catalogData : (catalogData?.data || []);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const timelineItems = [];
  
  catalog.forEach((item) => {
    // Only process upcoming releases
    if (item.status !== 'upcoming' && item.status !== 'demo') return;
    if (!item.release_date) return;
    
    const releaseDate = new Date(item.release_date);
    releaseDate.setHours(0, 0, 0, 0);
    
    // Only include future or today's releases
    if (releaseDate < today) return;
    
    const timeContext = getTimeContext(item.release_date);
    const dateGroup = getDateGroup(item.release_date);
    
    // Upcoming Releases
    if (settings.upcomingReleases) {
      timelineItems.push({
        id: `release-${item._id}`,
        type: 'release',
        title: item.song_title || 'Upcoming Release',
        description: `${item.song_title || 'Untitled'} by ${item.artist_name || 'Unknown Artist'}`,
        date: item.release_date,
        dateGroup,
        timeContext,
        status: item.status,
        color: '#17A2B8',
        actionUrl: '/catalog',
        actionLabel: 'Catalog',
        metadata: {
          artist: item.artist_name,
          songTitle: item.song_title,
          releaseDate: item.release_date,
          status: item.status
        }
      });
    }
    
    // Release Workflow Tasks
    if (settings.releaseWorkflowTasks) {
      // Check if there are workflow tasks related to this release
      // This would need to be integrated with the workflows/reminders system
      // For now, we'll add a placeholder that can be enhanced later
    }
  });
  
  return timelineItems;
};

/**
 * Process tasks/workflows for timeline
 */
const processTasksForTimeline = async (settings) => {
  if (!settings.tasksDeadlines) {
    return [];
  }
  
  const timelineItems = [];
  
  try {
    // Get reminders/tasks from reminders API
    const remindersResult = await remindersApi.getUserReminders();
    
    if (remindersResult.success && Array.isArray(remindersResult.reminders)) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      remindersResult.reminders.forEach((reminder) => {
        if (!reminder.dueDate) return;
        
        const dueDate = new Date(reminder.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        
        // Only include future or today's tasks
        if (dueDate < today) {
          // Include overdue tasks as well
          const timeContext = getTimeContext(reminder.dueDate);
          const dateGroup = dueDate.toDateString() === today.toDateString() ? 'Today' : 'Overdue';
          
          timelineItems.push({
            id: `task-${reminder._id}`,
            type: 'task',
            subType: 'overdue',
            title: reminder.title || 'Task',
            description: reminder.description || reminder.title || 'Task due',
            date: reminder.dueDate,
            dateGroup,
            timeContext: 'Overdue',
            status: 'overdue',
            color: '#FF3B30',
            actionUrl: '/tasks',
            actionLabel: 'Tasks',
            metadata: {
              title: reminder.title,
              description: reminder.description,
              dueDate: reminder.dueDate,
              priority: reminder.priority
            }
          });
        } else {
          const timeContext = getTimeContext(reminder.dueDate);
          const dateGroup = getDateGroup(reminder.dueDate);
          
          timelineItems.push({
            id: `task-${reminder._id}`,
            type: 'task',
            subType: 'upcoming',
            title: reminder.title || 'Task',
            description: reminder.description || reminder.title || 'Task due',
            date: reminder.dueDate,
            dateGroup,
            timeContext,
            status: 'due-soon',
            color: '#FF6B35',
            actionUrl: '/tasks',
            actionLabel: 'Tasks',
            metadata: {
              title: reminder.title,
              description: reminder.description,
              dueDate: reminder.dueDate,
              priority: reminder.priority
            }
          });
        }
      });
    }
  } catch (error) {
    console.error('Error processing tasks for timeline:', error);
  }
  
  return timelineItems;
};

/**
 * Process tour manager data for timeline (flights, hotels, transport)
 */
const processTourManagerForTimeline = async (settings) => {
  if (!settings.flights && !settings.hotels) {
    return [];
  }
  
  const timelineItems = [];
  
  try {
    const token = localStorage.getItem('dizeeToken');
    if (!token) return timelineItems;
    
    // Get tour manager shows (which contain flight/hotel data)
    const response = await getDataAPI('user/tm/shows', token);
    
    if (response?.data?.status === 200 && response.data.data) {
      const shows = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      shows.forEach((show) => {
        if (!show.showInfo?.perf_date) return;
        
        const perfDate = new Date(show.showInfo.perf_date);
        perfDate.setHours(0, 0, 0, 0);
        
        // Only include future or today's shows
        if (perfDate < today) return;
        
        // Process flights
        if (settings.flights && show.flights && Array.isArray(show.flights)) {
          show.flights.forEach((flight) => {
            if (!flight.departure_date) return;
            
            const flightDate = new Date(flight.departure_date);
            const timeContext = getTimeContext(flight.departure_date);
            const dateGroup = getDateGroup(flight.departure_date);
            
            timelineItems.push({
              id: `flight-${show._id}-${flight._id || Math.random()}`,
              type: 'flight',
              title: 'Flight',
              description: `${flight.origin || 'Origin'} → ${flight.destination || 'Destination'}`,
              date: flight.departure_date,
              dateGroup,
              timeContext,
              status: 'upcoming',
              color: '#FF9500',
              actionUrl: '/tour-manager/view-all',
              actionLabel: 'TourMngr',
              metadata: {
                origin: flight.origin,
                destination: flight.destination,
                departureDate: flight.departure_date,
                departureTime: flight.departure_time,
                arrivalDate: flight.arrival_date,
                arrivalTime: flight.arrival_time,
                airline: flight.airline,
                flightNumber: flight.flight_number,
                seat: flight.seat
              }
            });
          });
        }
        
        // Process hotels
        if (settings.hotels && show.hotels && Array.isArray(show.hotels)) {
          show.hotels.forEach((hotel) => {
            if (!hotel.check_in_date) return;
            
            const checkInDate = new Date(hotel.check_in_date);
            const timeContext = getTimeContext(hotel.check_in_date);
            const dateGroup = getDateGroup(hotel.check_in_date);
            
            timelineItems.push({
              id: `hotel-${show._id}-${hotel._id || Math.random()}`,
              type: 'hotel',
              title: 'Hotel',
              description: `${hotel.name || 'Hotel'} - ${hotel.city || 'City'}`,
              date: hotel.check_in_date,
              dateGroup,
              timeContext,
              status: 'upcoming',
              color: '#FF9500',
              actionUrl: '/tour-manager/view-all',
              actionLabel: 'TourMngr',
              metadata: {
                name: hotel.name,
                city: hotel.city,
                checkInDate: hotel.check_in_date,
                checkOutDate: hotel.check_out_date,
                address: hotel.address
              }
            });
          });
        }
      });
    }
  } catch (error) {
    console.error('Error processing tour manager data for timeline:', error);
  }
  
  return timelineItems;
};

/**
 * Get timeline data from all sources
 */
export const timelineApi = {
  async getTimeline() {
    try {
      const token = localStorage.getItem('dizeeToken');
      
      if (!token) {
        return {
          success: true,
          timeline: [],
          grouped: {}
        };
      }
      
      const settings = getTimelineSettings();
      
      // Fetch data from all sources in parallel
      const [
        bookingsResponse,
        catalogResponse,
        tasksResult,
        tourManagerResult
      ] = await Promise.allSettled([
        getDataAPI('user/getBookings', token),
        getDataAPI('user/getCatalog', token),
        processTasksForTimeline(settings),
        processTourManagerForTimeline(settings)
      ]);
      
      // Process bookings
      const bookings = bookingsResponse.status === 'fulfilled' && bookingsResponse.value?.data?.status === 200
        ? processBookingsForTimeline(bookingsResponse.value.data.data, settings)
        : [];
      
      // Process catalog
      const catalog = catalogResponse.status === 'fulfilled' && catalogResponse.value?.data?.status === 200
        ? processCatalogForTimeline(catalogResponse.value.data.data, settings)
        : [];
      
      // Process tasks (already processed)
      const tasks = tasksResult.status === 'fulfilled' ? tasksResult.value : [];
      
      // Process tour manager (already processed)
      const tourManager = tourManagerResult.status === 'fulfilled' ? tourManagerResult.value : [];
      
      // Combine all timeline items
      const allItems = [
        ...bookings,
        ...catalog,
        ...tasks,
        ...tourManager
      ];
      
      // Sort by date (earliest first)
      const sortedItems = allItems.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });
      
      // Group by dateGroup and sort items within each group
      const grouped = {};
      sortedItems.forEach((item) => {
        const group = item.dateGroup || 'Other';
        if (!grouped[group]) {
          grouped[group] = [];
        }
        grouped[group].push(item);
      });
      
      // Sort items within each group by date (earliest first)
      Object.keys(grouped).forEach((group) => {
        grouped[group].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        });
      });
      
      return {
        success: true,
        timeline: sortedItems,
        grouped
      };
    } catch (error) {
      console.error('Error fetching timeline:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch timeline',
        timeline: [],
        grouped: {}
      };
    }
  },
  
  /**
   * Get timeline settings
   */
  getSettings() {
    return getTimelineSettings();
  },
  
  /**
   * Save timeline settings
   */
  saveSettings(settings) {
    try {
      localStorage.setItem('timelineSettings', JSON.stringify(settings));
      return { success: true };
    } catch (error) {
      console.error('Error saving timeline settings:', error);
      return { success: false, error: error.message };
    }
  }
};

export default timelineApi;

